/**
 * PipelineBars - vertical bar chart of the pipeline stages.
 * Mobile keeps the headline funnel only (Tendering / Submitted / On site) — the
 * pre-start/final-account/retention/on-hold tail is noise on a narrow screen.
 * The full 7-stage breakdown reveals from a 640px container onward and holds
 * that layout (tighter gap/labels between 640-819px) until 820px, where it
 * settles back to its original, wider spacing — deferring the switch to the
 * compact 3-stage view for as long as the container can fit the full one
 * (Apple HIG, Layout: "defer switching to a compact view for as long as
 * possible"). Same rows drive both layouts.
 */

import { useMemo } from 'react';
import { cn } from '../../shared/ui/utils';
import { Skeleton } from '../../shared/ui/skeleton';
import type { Project } from '../../types/database';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { PipelineChart } from '../../shared-ui/home/PipelineChart';
import { buildPipelineRows, pipelineBarPercent } from '../../shared-ui/home/homeModels';
import { PIPELINE_BUCKETS } from './stages';

interface PipelineBarsProps {
  projects: Project[];
  compact?: boolean;
  /** Every stage as a labelled row, for a column too narrow for seven bars. */
  list?: boolean;
  loading?: boolean;
}

export function PipelineBars({ projects, compact = false, list = false, loading = false }: PipelineBarsProps) {
  // Counts, key stages and the bar scale are shared with the native app.
  const rows = useMemo(
    () => buildPipelineRows(projects).map((row, i) => ({ ...row, color: PIPELINE_BUCKETS[i].color })),
    [projects],
  );
  const isPhone = useMediaQuery('(max-width: 767px)');
  if (isPhone && !loading) return <PipelineChart projects={projects} />;

  if (list) {
    return (
      <ul className="flex flex-col gap-2.5 pb-1 pt-3 font-apple">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center gap-3">
            <span className="w-24 shrink-0 truncate text-[12px] text-muted-foreground">{r.label}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-foreground/5" aria-hidden="true">
              {!loading && (
                <div
                  className="h-full rounded-full transition-[width] duration-700 ease-out"
                  style={{ width: `${pipelineBarPercent(r.count, rows)}%`, background: r.color }}
                />
              )}
            </div>
            <span className="w-7 shrink-0 text-right text-[13px] font-semibold tabular-nums text-foreground">
              {loading ? '' : r.count}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className={cn(
      'px-5 pb-3 pt-5 font-apple @[700px]/dash:pb-4 @[700px]/dash:pt-7',
      compact && '@[700px]/dash:pt-5',
    )}>
      {/* Bars sit on a single continuous baseline; labels align in a row below.
          A floor on the height keeps the small stages legible next to a tall one.
          Tail bars are display:none below 640px so the visible three still
          spread evenly, and reveal as flex items from 640px up. Gap and label
          size tighten between 640-819px to fit all 7 stages, then relax back
          to their original values at 820px so the wide layout is unchanged.
          The max-w well keeps bar spacing tight on wide windows — evenly-flooded
          gaps between thin bars read as an empty chart. The border spans the full
          card width so the baseline stays continuous. */}
      <div className="border-b border-border/60">
        <div
          className={cn(
            'mx-auto flex h-[76px] w-full max-w-[720px] items-end justify-around gap-5 @[640px]/dash:h-[104px] @[640px]/dash:gap-2 @[820px]/dash:gap-5',
            compact && '@[640px]/dash:h-[84px]',
          )}
        >
          {rows.map((r) => {
            const pct = pipelineBarPercent(r.count, rows);
            return (
              <div
                key={r.key}
                className={cn(
                  'h-full flex-1 items-end justify-center',
                  r.isKey ? 'flex' : 'hidden @[640px]/dash:flex',
                )}
              >
                {loading ? (
                  <div className="relative h-3/5 w-full max-w-[60px]" aria-hidden="true">
                    <Skeleton className="h-full rounded-b-none rounded-t-[10px] bg-foreground/5 motion-reduce:animate-none @[640px]/dash:rounded-t-[12px]" />
                    <Skeleton className="absolute -top-6 left-1/2 h-4 w-5 -translate-x-1/2 bg-foreground/10 motion-reduce:animate-none @[640px]/dash:-top-7" />
                  </div>
                ) : <div
                  className="relative w-full max-w-[60px] rounded-t-[10px] shadow-[0_6px_12px_-9px_rgba(0,0,0,0.28)] transition-[height] duration-700 ease-out @[640px]/dash:rounded-t-[12px]"
                  style={{
                    height: `${pct}%`,
                    background: `linear-gradient(180deg, color-mix(in srgb, ${r.color} 88%, white 12%), ${r.color})`,
                  }}
                >
                  <span className="absolute -top-6 inset-x-0 text-center text-[16px] font-semibold tabular-nums text-foreground leading-none @[640px]/dash:-top-7 @[640px]/dash:text-[15px] @[820px]/dash:text-[18px]">
                    {r.count}
                  </span>
                </div>}
              </div>
            );
          })}
        </div>
      </div>
      <div className="mx-auto mt-2 flex w-full max-w-[720px] justify-around gap-5 @[640px]/dash:mt-2.5 @[640px]/dash:gap-2 @[820px]/dash:gap-5">
        {rows.map((r) => (
          <span
            key={r.key}
            className={cn(
              'flex-1 text-center text-[12px] text-muted-foreground leading-tight @[640px]/dash:text-[10px] @[820px]/dash:text-[12px]',
              r.isKey ? 'block' : 'hidden @[640px]/dash:block',
            )}
          >
            {r.label}
          </span>
        ))}
      </div>
    </div>
  );
}
